
import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { RouteWaypoint } from '../types';
import { Language, translations } from '../translations';
import { Link2, Save, Upload } from 'lucide-react';

interface RouteExportProps {
  route?: RouteWaypoint[];
  variants?: RouteWaypoint[][];
  activeVariantIdx?: number;
  lang: Language;
  onClose: () => void;
  user: any; // Added user prop
}

const RouteExport: React.FC<RouteExportProps> = ({ route, variants, activeVariantIdx, lang, onClose, user }) => {
  const [qrUrl, setQrUrl] = useState<string>('');
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(activeVariantIdx || 0);
  const [xctrackMode, setXctrackMode] = useState<'waypoints' | 'classic'>('waypoints');

  const originalRoute = variants && variants.length > 0
    ? variants[selectedVariantIdx]
    : route || [];

  const activeRoute = React.useMemo(() => {
    const maxPoints = xctrackMode === 'waypoints' ? 25 : 10;
    if (originalRoute.length <= maxPoints) return originalRoute;
    let res = [...originalRoute];
    while (res.length > maxPoints) {
      let minArea = Infinity;
      let minIdx = 1;
      for (let i = 1; i < res.length - 1; i++) {
        const p1 = res[i - 1];
        const p2 = res[i];
        const p3 = res[i + 1];
        const area = Math.abs((p1.lng * (p2.lat - p3.lat) + p2.lng * (p3.lat - p1.lat) + p3.lng * (p1.lat - p2.lat)) / 2);
        if (area < minArea) {
          minArea = area;
          minIdx = i;
        }
      }
      res.splice(minIdx, 1);
    }
    return res;
  }, [originalRoute, xctrackMode]);

  const getWaypointExportName = (wp: RouteWaypoint, idx: number, r: RouteWaypoint[]) => {
    if (idx === 0) return 'S: Start';
    if (idx === r.length - 1 && wp.type === 'landing') return 'Z: Ziel';
    const turnpointsBefore = r.slice(1, idx).filter(w => w.type !== 'landing');
    return `WP ${turnpointsBefore.length + 1}`;
  };

  const [trackName, setTrackName] = useState(`XC Trip ${new Date().toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}`);
  const [copySuccess, setCopySuccess] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  // ... (useEffect for QR remains same) ...
  const encodeXCTrackPolyline = (lon: number, lat: number, alt: number, radius?: number) => {
    const encodeValue = (val: number) => {
      let num = Math.round(val);
      num = num < 0 ? ~(num << 1) : (num << 1);
      let str = "";
      while (num >= 0x20) {
        str += String.fromCharCode((0x20 | (num & 0x1f)) + 63);
        num >>= 5;
      }
      str += String.fromCharCode(num + 63);
      return str;
    };
    let res = encodeValue(lon * 1e5) + encodeValue(lat * 1e5) + encodeValue(alt);
    if (radius !== undefined) {
      res += encodeValue(radius);
    }
    return res;
  };

  useEffect(() => {
    const generateQR = async () => {
      if (activeRoute.length === 0) return;
      let xcTask: any;
      
      if (xctrackMode === 'waypoints') {
        xcTask = {
          T: "W",
          V: 2,
          t: activeRoute.map((wp, idx) => ({
            z: encodeXCTrackPolyline(wp.lng, wp.lat, 0),
            n: getWaypointExportName(wp, idx, activeRoute).substring(0, 15)
          }))
        };
      } else {
        xcTask = {
          taskType: "CLASSIC",
          version: 2,
          t: activeRoute.map((wp, idx) => {
            let t = undefined;
            if (idx === 0) t = 2; // SSS
            else if (idx === activeRoute.length - 1) t = 3; // ESS
            
            const tp: any = {
              z: encodeXCTrackPolyline(wp.lng, wp.lat, 0, 400),
              n: getWaypointExportName(wp, idx, activeRoute).substring(0, 15)
            };
            if (t !== undefined) tp.t = t;
            return tp;
          }),
          s: {
            g: ["12:00:00Z"],
            t: 1 // RACE
          },
          g: {
            d: "23:00:00Z",
            t: 2 // CYLINDER
          }
        };
      }
      
      const jsonString = JSON.stringify(xcTask);
      let dataString = `XCTSK:${jsonString}`;

      try {
        const url = await QRCode.toDataURL(dataString, { margin: 2, scale: 10, errorCorrectionLevel: 'L', color: { dark: '#0f172a', light: '#ffffff' } });
        setQrUrl(url);
      } catch (err: any) { 
        console.error(err);
        if (err.message && err.message.includes('too big')) {
          setQrUrl('TOO_LONG');
        }
      }
    };
    generateQR();
  }, [activeRoute, trackName, xctrackMode]);

  const copyToClipboard = () => {
    const compactRoute = activeRoute.map(wp => ({ lat: wp.lat, lon: wp.lng, name: wp.name }));
    navigator.clipboard.writeText(JSON.stringify(compactRoute, null, 2));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleSaveToLibrary = async () => {
    if (!user) {
      alert("Bitte melde dich an, um Strecken permanent zu speichern.");
      return;
    }

    const newMission: any = {
      id: Math.random().toString(36).substr(2, 9),
      user_id: user.id, // DB expects user_id
      name: trackName,
      date: new Date().toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      timestamp: Date.now(),
      type: 'free',
      distance: 0,
      analysis: {
        text: 'Saved from Planner',
        sources: [],
        waypoints: activeRoute
      },
      isShared: false
    };

    // Calculate dist
    let d = 0;
    for (let i = 0; i < activeRoute.length - 1; i++) {
      const p1 = activeRoute[i]; const p2 = activeRoute[i + 1];
      const R = 6371;
      const dLat = (p2.lat - p1.lat) * Math.PI / 180;
      const dLon = (p2.lng - p1.lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      d += R * c;
    }
    newMission.distance = d;

    try {
      const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
      const res = await fetch('/api/user-missions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newMission)
      });

      if (res.ok) {
        setSavedSuccess(true);
        setTimeout(() => setSavedSuccess(false), 2000);
      } else {
        alert("Fehler beim Speichern in der Cloud.");
      }
    } catch (e) {
      console.error("Save failed", e);
      alert("Netzwerkfehler beim Speichern.");
    }
  };


  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateCUP = () => {
    if (activeRoute.length === 0) return;
    let cup = `name,code,country,lat,lon,elev,style,rwdir,rwlen,freq,desc\n`;
    activeRoute.forEach((wp, idx) => {
      const latAbs = Math.abs(wp.lat);
      const latDeg = Math.floor(latAbs);
      const latMin = ((latAbs - latDeg) * 60).toFixed(3).padStart(6, '0');
      const latStr = `${latDeg.toString().padStart(2, '0')}${latMin}${wp.lat >= 0 ? 'N' : 'S'}`;

      const lonAbs = Math.abs(wp.lng);
      const lonDeg = Math.floor(lonAbs);
      const lonMin = ((lonAbs - lonDeg) * 60).toFixed(3).padStart(6, '0');
      const lonStr = `${lonDeg.toString().padStart(3, '0')}${lonMin}${wp.lng >= 0 ? 'E' : 'W'}`;

      let type = 1;
      if (idx === 0) type = 2;
      if (idx === activeRoute.length - 1) type = 3;

      const safeName = (wp.name || `WP${idx+1}`).replace(/,/g, '');
      cup += `"${safeName}","${safeName}",,${latStr},${lonStr},0.0m,${type},,,,,\n`;
    });
    
    cup += `-----Related Tasks-----\n`;
    cup += `"${trackName}","${trackName}",`;
    activeRoute.forEach((wp, idx) => {
      const safeName = (wp.name || `WP${idx+1}`).replace(/,/g, '');
      cup += `"${safeName}",`;
    });
    cup += `\n`;
    
    downloadFile(cup, `${trackName.replace(/ /g, '_')}.cup`, 'text/plain');
  };

  const generateWPT = () => {
    if (activeRoute.length === 0) return;
    let wpt = `G  WGS 84\nU  1\n`;
    activeRoute.forEach((wp, idx) => {
      const safeName = (wp.name || `WP${idx+1}`);
      wpt += `W  ${safeName} A ${wp.lat.toFixed(6)} ${wp.lng.toFixed(6)} 27-MAR-10 00:00:00 0.000000\n`;
    });
    downloadFile(wpt, `${trackName.replace(/ /g, '_')}.wpt`, 'text/plain');
  };

  const generateXCTSK = () => {
    if (activeRoute.length === 0) return;
    let xcTask: any;
    
    if (xctrackMode === 'waypoints') {
      xcTask = {
        T: "W",
        V: 2,
        t: activeRoute.map((wp, idx) => ({
          z: encodeXCTrackPolyline(wp.lng, wp.lat, 0),
          n: wp.name || `WP${idx + 1}`
        }))
      };
    } else {
      xcTask = {
        taskType: "CLASSIC",
        version: 2,
        t: activeRoute.map((wp, idx) => {
          let t = undefined;
          if (idx === 0) t = 2; // SSS
          else if (idx === activeRoute.length - 1) t = 3; // ESS
          
          const tp: any = {
            z: encodeXCTrackPolyline(wp.lng, wp.lat, 0, 400),
            n: wp.name || `WP${idx + 1}`
          };
          if (t !== undefined) tp.t = t;
          return tp;
        }),
        s: {
          g: ["12:00:00Z"],
          t: 1 // RACE
        },
        g: {
          d: "23:00:00Z",
          t: 2 // CYLINDER
        }
      };
    }
    
    downloadFile(JSON.stringify(xcTask, null, 2), `${trackName.replace(/ /g, '_')}.xctsk`, 'application/json');
  };

  const openInXCTrack = async () => {
    if (activeRoute.length === 0) return;
    let xcTask: any;
    
    if (xctrackMode === 'waypoints') {
      xcTask = {
        T: "W",
        V: 2,
        t: activeRoute.map((wp, idx) => ({
          z: encodeXCTrackPolyline(wp.lng, wp.lat, 0),
          n: getWaypointExportName(wp, idx, activeRoute).substring(0, 15)
        }))
      };
    } else {
      xcTask = {
        taskType: "CLASSIC",
        version: 2,
        t: activeRoute.map((wp, idx) => {
          let t = undefined;
          if (idx === 0) t = 2; // SSS
          else if (idx === activeRoute.length - 1) t = 3; // ESS
          
          const tp: any = {
            z: encodeXCTrackPolyline(wp.lng, wp.lat, 0, 400),
            n: getWaypointExportName(wp, idx, activeRoute).substring(0, 15)
          };
          if (t !== undefined) tp.t = t;
          return tp;
        }),
        s: {
          g: ["12:00:00Z"],
          t: 1 // RACE
        },
        g: {
          d: "23:00:00Z",
          t: 2 // CYLINDER
        }
      };
    }
    
    const jsonString = JSON.stringify(xcTask);
    let dataString = `XCTSK:${jsonString}`;
    
    window.location.href = `intent://xctrack.org/#Intent;scheme=xctrack;action=android.intent.action.VIEW;S.task=${encodeURIComponent(dataString)};end`;
  };

  const generateGPX = () => {
    if (activeRoute.length === 0) return;
    let gpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="Streckenfliegen.app">\n`;
    activeRoute.forEach((wp, idx) => {
        gpx += `  <wpt lat="${wp.lat.toFixed(6)}" lon="${wp.lng.toFixed(6)}"><name>${getWaypointExportName(wp, idx, activeRoute)}</name></wpt>\n`;
    });
    gpx += `  <rte>\n    <name>${trackName}</name>\n`;
    activeRoute.forEach((wp, idx) => {
        gpx += `    <rtept lat="${wp.lat.toFixed(6)}" lon="${wp.lng.toFixed(6)}"><name>${getWaypointExportName(wp, idx, activeRoute)}</name></rtept>\n`;
    });
    gpx += `  </rte>\n</gpx>`;
    downloadFile(gpx, `${trackName.replace(/ /g, '_')}.gpx`, 'application/gpx+xml');
  };

  const handleShare = () => {
    const shareLink = `https://xc-tracks.ch/share/${Math.random().toString(36).substr(2, 6)}`;
    navigator.clipboard.writeText(shareLink);
    setShareSuccess(true);
    setTimeout(() => setShareSuccess(false), 2000);
  };

  const isDe = lang === 'de';

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div className="dark-glass max-w-md w-full p-8 rounded-[3rem] border border-white/10 shadow-[0_0_80px_rgba(0,0,0,0.6)] text-center relative overflow-hidden flex flex-col max-h-[90vh]">
        {/* Glows */}
        <div className="absolute -top-20 -right-20 w-48 h-48 bg-sky-600/20 rounded-full blur-[80px]"></div>
        <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-indigo-600/20 rounded-full blur-[80px]"></div>

        <div className="flex justify-between items-center mb-4 relative z-10 shrink-0">
          <div className="text-left flex-1 pr-4">
            <h3 className="text-xl font-black text-white uppercase tracking-tight">
              {isDe ? 'Route Speichern' : 'Save Route'}
            </h3>
            <p className="text-[10px] font-bold text-sky-500 uppercase tracking-widest mb-2">Library & Export</p>
            
            {variants && variants.length > 1 && (
              <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-1">
                {variants.map((v, idx) => (
                  <button 
                    key={idx}
                    onClick={() => setSelectedVariantIdx(idx)}
                    className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors ${selectedVariantIdx === idx ? 'bg-sky-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                  >
                    Variante {idx + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all active:scale-90">✕</button>
        </div>

        <div className="space-y-4 relative z-10 flex-1 flex flex-col min-h-0 overflow-y-auto custom-scrollbar">
          {/* Name Input */}
          <div className="shrink-0 space-y-3">
            <input
              type="text"
              value={trackName}
              onChange={e => setTrackName(e.target.value)}
              className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-white font-bold focus:ring-2 focus:ring-sky-500 outline-none text-center text-lg"
              placeholder="Track Name"
            />
            <div className="flex bg-slate-900/50 p-1 rounded-xl border border-white/5">
              <button 
                onClick={() => setXctrackMode('waypoints')}
                className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all ${xctrackMode === 'waypoints' ? 'bg-sky-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
              >
                Freier Flug <span className="block text-[8px] opacity-70 normal-case">(Linie)</span>
              </button>
              <button 
                onClick={() => setXctrackMode('classic')}
                className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all ${xctrackMode === 'classic' ? 'bg-sky-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
              >
                Wettbewerb <span className="block text-[8px] opacity-70 normal-case">(Zylinder)</span>
              </button>
            </div>
          </div>

          {/* Large QR Code Area */}
          <div className="bg-white p-4 rounded-[2rem] shadow-inner shrink-0 relative group">
            {qrUrl === 'TOO_LONG' ? (
              <div className="w-full aspect-square flex flex-col items-center justify-center p-4 text-center">
                <span className="text-4xl mb-4">⚠️</span>
                <p className="text-xs font-bold text-slate-800 uppercase tracking-widest leading-relaxed">
                  Route zu lang für QR-Code
                </p>
                <p className="text-[10px] text-slate-500 mt-2">
                  Bitte nutze den Dateidownload oder den Android Intent.
                </p>
              </div>
            ) : qrUrl ? (
              <img src={qrUrl} alt="Route QR Code" className="w-full aspect-square rounded-xl object-contain mix-blend-multiply" />
            ) : (
              <div className="w-full aspect-square flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-sky-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-center mt-2">{isDe ? 'Scan für XCTrack und SeeYou Navigator' : 'Scan for XCTrack and SeeYou Navigator'}</p>

            {/* Copy Overlay on Hover */}
            <div className="absolute inset-0 bg-slate-900/90 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-[2rem]">
              <button onClick={copyToClipboard} className="bg-white text-slate-900 px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:scale-105 transition-transform">
                {copySuccess ? (isDe ? 'Kopiert!' : 'Copied!') : (isDe ? 'JSON Kopieren' : 'Copy JSON')}
              </button>
            </div>
          </div>

          {/* Export Options */}
          <div className="shrink-0 bg-slate-900/50 rounded-[1.5rem] border border-white/5 p-2 space-y-1">
            <button onClick={generateCUP} className="w-full text-left px-4 py-2 hover:bg-white/5 rounded-xl transition-colors text-sm font-bold text-slate-300 flex items-center justify-between">
              <span>Skytraxx 4.0 / 5.0 <span className="text-[10px] text-slate-500 ml-1 font-normal">(Verzeichnis Routes)</span></span>
              <span className="text-xs bg-slate-800 px-2 py-0.5 rounded text-slate-500">.CUP</span>
            </button>
            <button onClick={generateWPT} className="w-full text-left px-4 py-2 hover:bg-white/5 rounded-xl transition-colors text-sm font-bold text-slate-300 flex items-center justify-between">
              <span>Skytraxx 2.1 / 3.0 <span className="text-[10px] text-slate-500 ml-1 font-normal">(Verzeichnis Routes)</span></span>
              <span className="text-xs bg-slate-800 px-2 py-0.5 rounded text-slate-500">.WPT</span>
            </button>
            <button onClick={generateCUP} className="w-full text-left px-4 py-2 hover:bg-white/5 rounded-xl transition-colors text-sm font-bold text-slate-300 flex items-center justify-between">
              <span>Naviter Oudie</span>
              <span className="text-xs bg-slate-800 px-2 py-0.5 rounded text-slate-500">.CUP</span>
            </button>
            <button onClick={generateXCTSK} className="w-full text-left px-4 py-2 hover:bg-white/5 rounded-xl transition-colors text-sm font-bold text-slate-300 flex items-center justify-between">
              <span>XC Track <span className="text-[10px] text-slate-500 ml-1 font-normal">(Datei Download)</span></span>
              <span className="text-xs bg-slate-800 px-2 py-0.5 rounded text-slate-500">.XCTSK</span>
            </button>
            <button onClick={generateGPX} className="w-full text-left px-4 py-2 hover:bg-white/5 rounded-xl transition-colors text-sm font-bold text-slate-300 flex items-center justify-between">
              <span>Garmin / Coros / Standard GPX</span>
              <span className="text-xs bg-slate-800 px-2 py-0.5 rounded text-slate-500">.GPX</span>
            </button>
            <button onClick={openInXCTrack} className="w-full text-left px-4 py-2 hover:bg-white/5 rounded-xl transition-colors text-sm font-bold text-sky-400 flex items-center justify-between">
              <span>In XC Track öffnen <span className="text-[10px] text-sky-500/50 ml-1 font-normal">(Android Intent)</span></span>
              <span className="text-xs bg-sky-500/20 px-2 py-0.5 rounded text-sky-500">OPEN</span>
            </button>
          </div>


        </div>
      </div>
    </div>
  );
};

export default RouteExport;
